import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';

interface Breadcrumb {
  label: string;
  path: string;
  icon?: string;
}

interface BreadcrumbContextType {
  customBreadcrumbs: Breadcrumb[] | null;
  setCustomBreadcrumbs: (breadcrumbs: Breadcrumb[] | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export const BreadcrumbProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customBreadcrumbs, setCustomBreadcrumbs] = useState<Breadcrumb[] | null>(null);

  // Wrapper to ensure breadcrumbs are always replaced synchronously and never accumulated
  const setBreadcrumbs = useCallback((breadcrumbs: Breadcrumb[] | null) => {
    // Always replace, never accumulate - use functional form to ensure latest value
    // Also ensure no duplicates by checking if breadcrumbs is an array
    if (breadcrumbs && Array.isArray(breadcrumbs)) {
      // Remove duplicates based on path + label combination
      const uniqueBreadcrumbs = breadcrumbs.filter((crumb, index, self) => 
        index === self.findIndex((c) => c.path === crumb.path && c.label === crumb.label)
      );
      setCustomBreadcrumbs(uniqueBreadcrumbs);
    } else {
      setCustomBreadcrumbs(breadcrumbs);
    }
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ customBreadcrumbs, setCustomBreadcrumbs: setBreadcrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
};

